import React, { useContext, useEffect, useState } from "react";
import { ContainerCupomListagem } from "./styles";
import { ConfiguracoesContext } from "../../../contexts/Configuracoes/ConfiguracoesContext";
import Titulo from "../../Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { Toggle } from "../../Toggle/Toggle";
import { copy } from "../../../services/Functions";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { currencyMask, dateMask, percentageMask } from "../../../services/Masks";
import { AuthContext } from "../../../contexts/Auth/AuthContext";
import Table from "../../Table/Table";
import { Button } from "../../Button";
import { Select } from "../../Select/index";
import Paginacao from "../../Paginacao";
import { Input } from "../../Input";

interface CupomListagemProps {
  onEditarCupom: (index: number) => void;
  configIn?: "empresa" | "configuracoes";
  onNovoCupom?: () => void;
  mostrarTexto?: boolean;
  apenasPerenes?: boolean;
}

function normalizarBusca(texto: string) {
  return texto.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

const CupomListagem: React.FC<CupomListagemProps> = ({
  configIn = "empresa",
  onEditarCupom,
  onNovoCupom,
  mostrarTexto = true,
  apenasPerenes = false,
}) => {
  const { cupons, setCupons } = useContext(ConfiguracoesContext);

  const { permissoesPlanos } = useContext(AuthContext);
  const [permitido, setPermitido] = React.useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itensPorPagina] = useState(20);
  const [filtroAtivo, setFiltroAtivo] = useState<'todos' | 'ativos' | 'inativos'>('ativos');
  const [busca, setBusca] = useState("");
  const [buscaDigitada, setBuscaDigitada] = useState("");

  const isCupomAtivo = (ativo: any): boolean => {
    return ativo === true || ativo === 1 || ativo === "1" || ativo === "true";
  };

  // Cupom "perene": sem data de expiracao e sem limite de uso.
  const isCupomPerene = (cupom: any): boolean => {
    const semExpiracao =
      cupom.dataExpiracao == null || cupom.dataExpiracao === "";
    const semLimite =
      cupom.quantidadeLimite == null || cupom.quantidadeLimite === 0;
    return semExpiracao && semLimite;
  };

  const tableColumns = [
    { 
      key: "cupom", 
      label: "Cupom",
      mask: (value: string) => (
        <span 
          onClick={() => {
            const index = cupons.findIndex(c => c.cupom === value);
            if (index !== -1) {
              onEditarCupom(index);
            }
          }}
          style={{ 
            cursor: 'pointer', 
          }}
          className="fonteTexto"
        >
          {value}
        </span>
      )
    },
    { 
      key: "tipo_desconto", 
      label: "Tipo",
      mask: (value: string) => value === "porcentagem" ? "% Porcentagem" : "R$ Valor fixo"
    },
    { 
      key: "descontoFormatado", 
      label: "Desconto",
      mask: (value: any) => value
    },
    { 
      key: "dataExpiracao", 
      label: "Validade",
      mask: (value: string) => value ? dateMask(value, undefined, true) : "Sem expiração"
    },
    { 
      key: "quantidadeInfo", 
      label: "Uso",
      mask: (value: string) => value
    },
    { 
      key: "acoes", 
      label: "Ações",
      mask: (value: any) => value
    }
  ];

  const cuponsFiltrados = cupons.filter(cupom => {
    if (apenasPerenes && !isCupomPerene(cupom)) {
      return false;
    }
    if (filtroAtivo === 'ativos') {
      return isCupomAtivo(cupom.ativo);
    } else if (filtroAtivo === 'inativos') {
      return !isCupomAtivo(cupom.ativo);
    }
    return true;
  }).filter(cupom => {
    const termo = normalizarBusca(busca);
    return !termo || normalizarBusca(cupom.cupom || "").includes(termo);
  });

  // ordena pelo nome do cupom antes de paginar, para que a ordem seja a mesma em todas as páginas
  const cuponsOrdenados = [...cuponsFiltrados].sort((a, b) =>
    (a.cupom || "").toString().localeCompare((b.cupom || "").toString(), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );

  const totalPaginas = Math.max(1, Math.ceil(cuponsOrdenados.length / itensPorPagina));

  useEffect(() => {
    setCurrentPage(1);
  }, [filtroAtivo, busca]);

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(totalPaginas);
  }, [currentPage, totalPaginas]);

  const tableData = cuponsOrdenados.map((cupom) => {
    const originalIndex = cupons.findIndex(c => c === cupom);
    return {
      ...cupom,
      quantidadeInfo: (cupom.quantidadeLimite == null || cupom.quantidadeLimite === 0)
        ? 'Ilimitado'
        : `${cupom.quantidadeUso || 0} de ${cupom.quantidadeLimite}`,
      acoes: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Toggle
            value={cupom.ativo}
            onChange={(newValue) => {
              const copia = [...cupons];
              copia[originalIndex].ativo = newValue;
              setCupons(copia);
            }} 
          />
          <span
            onClick={() => onEditarCupom(originalIndex)}
            className="editar fonteTexto"
          >
            Editar
          </span>
        </div>
      ),
      _originalIndex: originalIndex,
      descontoFormatado: cupom.tipo_desconto === "valor"
        ? currencyMask(cupom.desconto.toString())
        : `${percentageMask(cupom.desconto.toString())}`
    };
  });

  const dadosPagina = tableData.slice(
    (currentPage - 1) * itensPorPagina,
    currentPage * itensPorPagina
  );
  useEffect(() => {
    if(permissoesPlanos["CUPOM_DE_DESCONTO"]?.permitido){
      setPermitido(true);
    }
    else{
      setPermitido(false);
    }
  }, [permissoesPlanos["CUPOM_DE_DESCONTO"]?.permitido]);

  const { theme } = useTheme();
  const mostrarBotaoNovo = configIn === "empresa" && permitido;

  return (
    <>
      <ContainerCupomListagem theme={theme} configIn={configIn}>
        <div className="containerCupomListagem">
          {mostrarTexto && (
            <>
              <div className="textoCupom fonteTexto">
                Crie cupons de desconto para atrair clientes e aumentar vendas.
                É simples: escolha um nome, defina valor fixo ou porcentagem e ajuste as regras de uso. <br />
                Ofereça promoções exclusivas e crie urgência para incentivar compras rápidas.
              </div>
              <div className="linha"></div>
            </>
          )}

          {cupons.length > 0 && (
            <div className="filtros-cupom">
              <div className="busca-listagem">
                <Input
                  type="text"
                  icone="icons/search.svg"
                  placeholder="Pesquisar por cupom"
                  value={buscaDigitada}
                  onChange={(newValue) => setBuscaDigitada(newValue)}
                  onDebounce={setBusca}
                  debounceTime={1000}
                  onKeyDown={(event) => {
                    // a listagem fica dentro do form de configurações; Enter não deve salvar
                    if (event.key === "Enter") event.preventDefault();
                  }}
                />
              </div>
              <Select
                value={filtroAtivo}
                onChange={(newValue) => setFiltroAtivo(newValue as 'todos' | 'ativos' | 'inativos')}
                options={[
                  { name: "Ativos", value: "ativos" },
                  { name: "Inativos", value: "inativos" },
                  { name: "Todos", value: "todos" }
                ]}
                className="select-filtro-cupons"
              />
            </div>
          )}
   
          {tableData.length > 0 ? (
            <Table
              columns={tableColumns}
              data={dadosPagina}
              sortable={true}
              selectable={false}
              button={false}
              page={1}
              itensPorPagina={itensPorPagina}
            />
          ) : (
            <div className="fonteTexto" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              {cupons.length === 0 
                ? 'Nenhum cupom cadastrado' 
                : busca.trim()
                ? 'Nenhum cupom encontrado para a pesquisa'
                : `Nenhum cupom ${filtroAtivo === 'ativos' ? 'ativo' : 'inativo'} encontrado`
              }
            </div>
          )}
          {(totalPaginas > 1 || mostrarBotaoNovo) && (
            <div className="rodapeListagem">
              <div className="paginacaoListagem">
                {totalPaginas > 1 && (
                  <Paginacao
                    currentPage={currentPage}
                    totalPages={totalPaginas}
                    onPageChange={setCurrentPage}
                  />
                )}
              </div>
              <div className="adicionarListagem">
                {mostrarBotaoNovo && (
                  <div
                    className="novo fonteTexto"
                    onClick={() => {
                      { 
                        if (!permissoesPlanos["CUPOM_DE_DESCONTO"]?.permitido) {
                          return;
                        }
                      }
                      onNovoCupom && onNovoCupom();
                    }}
                  >
                    Adicionar um novo cupom
                  </div>
                )}
              </div>
              <div></div>
            </div>
          )}
        </div>
      </ContainerCupomListagem>
    </>
  );
};
export default CupomListagem;
